import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { TextField, Grid, Autocomplete } from '@mui/material';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import CustomModal, {
  CustomModalBasicProps,
  GridItem,
  GridSection,
  gridItemColumnSize,
} from 'src/components/CustomModal';
import { WrappedPlanDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PlanDefinition';
import { onSuccess } from 'src/@types/crs/case';
import useObjectState from 'src/hooks/useObjectState';
import DatePickerMoment from 'src/components/DatePickerMoment';
import moment, { DurationInputArg2 } from 'moment';
import {
  Coding,
  GoalTarget,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import * as goalApiHelpers from './goal.api';
import { useSnackbar } from 'notistack';
import { WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import SearchMember, {
  SearchMemberProps,
} from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import {
  TaskSelectedMemberOption,
  TaskSelectedMemberState,
  TaskSelectedMemberValue,
  getOwnerDisplayLabel,
} from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import {
  getConditionsByGoal,
  getObservationsByGoal,
  getValueSetConceptValue,
} from 'src/careflow/helpers/common';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useGoalContext from 'src/careflow/care-coordination/case/goal/hook/useGoalContext';
import GoalInfoRibbon from './GoalInfoRibbon';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import SelectSearchMember from 'src/careflow/common/SelectSearchMember';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import TaskSuggestionsGrid, { TaskSuggestion } from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskSuggestionsGrid';
import GoalTargets from './GoalTarget';
import ExpandableCard from 'src/components/common/ExpandableCard';
import { deepIsEmpty } from 'src/utils/fhir';

export interface ActionData {
  payload: any;
  successfulMessage: string;
  actionCall: Function;
}
export interface GoalModalProps extends CustomModalBasicProps {
  pathways: ValueSetComposeIncludeConcept[];
  onSuccessfulCreation: onSuccess;
  onSuccessfulEdit: onSuccess;
  patient: WrappedPatient | null;
  goalToEdit: WrappedGoal | null;
  planDefinitions: WrappedPlanDefinition[];
  conditions: WrappedCondition[];
  observations: WrappedObservation[];
  carePlan?: WrappedCarePlan | null;
  defaultPathway?: ValueSetComposeIncludeConcept;
  defaultAddresses?: (WrappedCondition | WrappedObservation)[];
  defaultPlanDefinition?: WrappedPlanDefinition;
}

export interface GoalModalState {
  selectedPlanDefinition: WrappedPlanDefinition | null;
  selectedConditions: WrappedCondition[];
  selectedObservations: WrappedObservation[];
  selectedAddresses: (WrappedCondition | WrappedObservation)[];
  selectedLifeCycleStatus: GoalLifeCycleStatusOption | null;
  selectedAchievementStatus: GoalAchievementStatusOption | null;
  selectedPathway: ValueSetComposeIncludeConcept | null;
  description: string;
  comments: string;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  isLoading: boolean;
  taskOwner: TaskSelectedMemberValue | null;
  taskSuggestions: TaskSuggestion | null;
  target: GoalTarget[] | null;
}
export interface ErrorFieldState {
  owner?: string | null;
  lifecycleStatus?: string | null;
  category?: string | null;
  startDate?: string | null;
  description?: string | null;
  pathway?: string | null;
  endDate?: string | null;
}

export interface GoalDataFromPlanDefinition {
  lifecycleStatus: ValueSetComposeIncludeConcept | null;
  achievementStatus: Coding | undefined;
  goalStartDate: moment.Moment | null;
  goalEndDate: moment.Moment | null;
  description: string;
}

const getInitialState = ({
  defaultPathway,
  goalToEdit,
  pathways,
  goalStatuses,
  conditions,
  observations,
  defaultAddresses,
  defaultPlanDefinition,
}: {
  defaultPathway: ValueSetComposeIncludeConcept | null | undefined;
  goalToEdit: WrappedGoal | null | undefined;
  pathways: ValueSetComposeIncludeConcept[];
  conditions: WrappedCondition[];
  observations: WrappedObservation[];
  goalStatuses: GoalLifeCycleStatusOption[];
  defaultAddresses?: (WrappedCondition | WrappedObservation)[];
  defaultPlanDefinition?: WrappedPlanDefinition;
}): GoalModalState => {
  const lifecycleStatus = getValueSetConceptValue(goalStatuses ?? [], goalToEdit?.lifecycleStatus);
  const achievementStatus = goalToEdit?.getAchievementStatus?.();

  let startDate: moment.Moment | null = moment();
  let endDate: moment.Moment | null = null;

  if (goalToEdit) {
    startDate = moment(goalToEdit?.startDate ?? null).isValid()
      ? moment(goalToEdit?.startDate)
      : null;
    endDate = moment(goalToEdit?.getEndDate?.() ?? null).isValid()
      ? moment(goalToEdit?.getEndDate?.())
      : null;
  }
  const pathway = goalToEdit?.getPathway()
    ? pathways.find(({ code }) => code === goalToEdit?.getPathway())
    : null;

  let initialConditions: WrappedCondition[] = [];

  const existingConditionRefs = goalToEdit?.addresses?.filter(
    (x) => x?.reference?.startsWith('Condition') ?? []
  );

  if (goalToEdit && existingConditionRefs && existingConditionRefs.length > 0) {
    initialConditions = getConditionsByGoal(goalToEdit, conditions);
  }

  let initialObservations: WrappedObservation[] = [];

  const existingObservationRefs = goalToEdit?.addresses?.filter(
    (x) => x?.reference?.startsWith('Observation') ?? []
  );

  if (goalToEdit && existingObservationRefs && existingObservationRefs.length > 0) {
    initialObservations = getObservationsByGoal(goalToEdit, observations); // TODO: getObservationsByGoal
  }

  let inititialAddresses: (WrappedCondition | WrappedObservation)[] = [];
  inititialAddresses.push(...initialObservations);
  inititialAddresses.push(...initialConditions);

  if (!!defaultAddresses?.length) inititialAddresses = [...defaultAddresses];

  let description = goalToEdit?.getPlainDescription?.() ?? '';
  let dataFromPlanDefinition: GoalDataFromPlanDefinition | null = null;
  if (defaultPlanDefinition && !goalToEdit) {
    dataFromPlanDefinition = getDataFromPlanDefinition(
      goalStatuses,
      defaultPlanDefinition,
      startDate
    );
  }

  return {
    startDate: dataFromPlanDefinition?.goalStartDate ?? startDate,
    endDate: dataFromPlanDefinition?.goalEndDate ?? endDate,
    description: dataFromPlanDefinition?.description ?? description,
    selectedLifeCycleStatus: dataFromPlanDefinition?.lifecycleStatus ?? lifecycleStatus ?? null,
    selectedAchievementStatus:
      dataFromPlanDefinition?.achievementStatus ?? achievementStatus ?? null,
    selectedPathway: pathway ?? defaultPathway ?? null,
    comments: goalToEdit?.getComments?.() ?? '',
    selectedPlanDefinition: defaultPlanDefinition ? defaultPlanDefinition : null,
    selectedConditions: initialConditions,
    selectedObservations: initialObservations,
    selectedAddresses: inititialAddresses,
    isLoading: false,
    taskOwner: null,
    taskSuggestions: null,
    target: goalToEdit?.target ?? null,
  };
};

const initialErrorState: ErrorFieldState = {
  owner: null,
  lifecycleStatus: null,
  category: null,
  startDate: null,
  description: null,
  pathway: null,
  endDate: null,
};

const getInitialTaskOwnerState = () => ({
  isSearchMemberOpen: false,
  isTaskOwnerOpen: false,
  selectedRequester: null,
  selectedTaskOwner: null,
  selectedRequesterPerformer: null,
  selectedPerformer: null,
  inputSelected: null
});

const GoalModal = ({
  patient,
  onClose,
  open,
  pathways,
  defaultPathway,
  carePlan,
  onSuccessfulCreation,
  onSuccessfulEdit,
  goalToEdit,
  conditions,
  observations,
  planDefinitions,
  defaultAddresses,
  defaultPlanDefinition,
  ...goalModalProps
}: GoalModalProps): JSX.Element => {
  const {
    valueSets: [goalStatuses, goalAchievementStatuses],
  } = useValueSetsByIdentifiers(['ph-goal-lifecycle-status', 'ph-goal-achievement-status']);
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();
  const { relatedGoals } = useGoalContext();
  const { componentsData, configurations } = useTenantConfigData();
  const { searchMemberOptions } = componentsData?.searchMember ?? {};

  const parentGoal = relatedGoals?.[0]?.wrappedGoal;
  const [
    {
      selectedPlanDefinition,
      selectedConditions,
      selectedObservations,
      selectedAddresses,
      selectedLifeCycleStatus,
      selectedAchievementStatus,
      selectedPathway,
      description,
      comments,
      startDate,
      endDate,
      isLoading,
      taskOwner,
      taskSuggestions, 
      target
    },
    updateState,
  ] = useObjectState<GoalModalState>(
    getInitialState({
      defaultPathway,
      goalToEdit,
      pathways,
      conditions,
      observations,
      goalStatuses: [],
    })
  );

  const [{ isSearchMemberOpen, selectedTaskOwner }, updateTaskOwner] =
    useObjectState<TaskSelectedMemberState>(getInitialTaskOwnerState());

  const [{ isTaskSuggestionsOpen }, updateTaskSuggestions] =
    useObjectState<{isTaskSuggestionsOpen: boolean}>({ isTaskSuggestionsOpen: false });

  const [errors, updateErrorFieldState] = useObjectState<ErrorFieldState>(initialErrorState);

  const prevOpenRef = useRef(false);
  const prevGoalIdRef = useRef<string | undefined>(undefined);
  const latestTargetRef = useRef<GoalModalState['target']>(null);

  const updateStateForGoalTargets = useCallback((patch: Partial<GoalModalState>) => {
    if (patch.target !== undefined) {
      latestTargetRef.current = patch.target;
      const firstDue = patch.target?.[0]?.dueDate;
      const endDateFromTarget =
        firstDue && moment(firstDue).isValid() ? moment(firstDue) : null;
      updateState({ ...patch, endDate: endDateFromTarget });
      return;
    }
    updateState(patch);
  }, [updateState]);

  const updateStateWithEndDate = useCallback(
    (endDateValue: moment.Moment | null) => {
      const dueDateStr = endDateValue?.isValid?.() ? endDateValue.format('YYYY-MM-DD') : undefined;
      const baseTargets = target && target.length > 0 ? target : [{}];
      const newTargets = baseTargets.map((t, i) =>
        i === 0 ? { ...t, dueDate: dueDateStr } : { ...t }
      );
      latestTargetRef.current = newTargets;
      updateState({ endDate: endDateValue, target: newTargets });
    },
    [updateState, target]
  );

  const compareByGoalDescription = function (a, b) {
    const aDesc = (a.getGoalDescription?.() ?? '').toLowerCase();
    const bDesc = (b.getGoalDescription?.() ?? '').toLowerCase();
    return aDesc.localeCompare(bDesc);
  };
  const filteredPlanDefinitions = useMemo(() => {
    const planDefinitionsFiltered = selectedPathway?.code
      ? planDefinitions.filter((p) => p?.getGoalCategory?.() === selectedPathway?.code)
      : planDefinitions;

    const sortedPlanDefinitions = planDefinitionsFiltered.sort(compareByGoalDescription);
    if (
      defaultPlanDefinition &&
      !sortedPlanDefinitions.find(({ id }) => id === defaultPlanDefinition.id)
    )
      return [defaultPlanDefinition, ...sortedPlanDefinitions];
    return sortedPlanDefinitions;
  }, [planDefinitions, selectedPathway, defaultPlanDefinition]);

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      prevGoalIdRef.current = undefined;
      updateTaskSuggestions({ isTaskSuggestionsOpen: false });
      return;
    }
    const goalId = goalToEdit?.id;
    const justOpened = !prevOpenRef.current;
    const goalChanged = prevGoalIdRef.current !== goalId;
    prevOpenRef.current = true;
    prevGoalIdRef.current = goalId;

    if (justOpened || goalChanged) {
      const initialState = getInitialState({
        defaultPathway,
        goalToEdit,
        pathways,
        conditions,
        observations,
        goalStatuses: goalStatuses?.asList?.() ?? [],
        defaultAddresses,
        defaultPlanDefinition,
      });
      latestTargetRef.current = initialState.target ?? null;
      updateState(initialState);
      if (!goalToEdit && defaultPlanDefinition)
        updateTaskSuggestions({ isTaskSuggestionsOpen: true });
      updateErrorFieldState(initialErrorState);
      updateTaskOwner(getInitialTaskOwnerState());
    }
  }, [
    goalStatuses,
    pathways,
    conditions,
    open,
    defaultPathway,
    goalToEdit,
    updateErrorFieldState,
    updateState,
    updateTaskOwner,
    defaultAddresses,
    defaultPlanDefinition,
    observations,
  ]);

  const sortedPathways = useMemo(() => {
    return [...pathways].sort((a, b) => {
      const displayA = (a.display || '').toLowerCase();
      const displayB = (b.display || '').toLowerCase();
      return displayA.localeCompare(displayB);
    });
  }, [pathways]);

  const addresses = useMemo(() => {
    const getAddressLabel = (option: WrappedCondition | WrappedObservation) => {
      let baseLabel = '';
      let priorityInfo = '';
      if (option.resourceType === 'Observation' && option.valueCodeableConcept) {
        baseLabel = option?.valueCodeableConcept ? 
          option.getDisplayValueFromValueCodeableConcept() : "";
        const interpretation = option?.interpretation ? 
          option.getInterpretationDisplay() : null;
        if (interpretation) {
          priorityInfo = ` (${interpretation})`;
        }
      } else if (option.resourceType === 'Condition') {
        baseLabel = option.code?.text ?? '';
        const severity = option?.severity && typeof option?.getSeverityDisplay === 'function'
          ? option.getSeverityDisplay()
          : null;
        if (severity) {
          priorityInfo = ` (${severity})`;
        }
      } else {
        baseLabel = option.code?.text ?? '';
      }
      return (baseLabel + priorityInfo).toLowerCase();
    };

    const combinedAddresses = [...conditions, ...observations];
    return combinedAddresses.sort((a, b) => {
      const labelA = getAddressLabel(a);
      const labelB = getAddressLabel(b);
      return labelA.localeCompare(labelB);
    });
  }, [conditions, observations]);

  const handleOnSave = async () => {
    const errors: ErrorFieldState = {};
    updateErrorFieldState(initialErrorState);
    if (!selectedLifeCycleStatus) {
      errors.lifecycleStatus = 'Must select a valid status';
    }
    if (startDate && endDate && endDate?.isBefore(startDate, 'date')) {
      errors.startDate = "Start Date can't be less than End Date";
    }
    if (startDate && !startDate?.isValid()) {
      errors.startDate = 'Must specify a valid date';
    }
    if (endDate && !endDate?.isValid()) {
      errors.endDate = 'Must specify a valid date';
    }
    if (!description) {
      errors.description = 'Must specify a description';
    }
    if (Object.keys(errors).length) {
      updateErrorFieldState({ ...errors });
      return;
    }
    updateState({ isLoading: true });
    const isCreating = !goalToEdit;
    const { payload, actionCall, successfulMessage } = getGoalPayload(isCreating);
    const response = !goalToEdit
      ? await goalApiHelpers.createGoal(payload)
      : await goalApiHelpers?.editGoal(payload, goalToEdit?.id as string);
    updateState({ isLoading: false });
    if (!response) {
      enqueueSnackbar("There's been an error. Please Try Again", { variant: 'error' });
      return;
    }
    enqueueSnackbar(successfulMessage);
    actionCall?.(response);
    onClose?.({}, 'backdropClick');
  };

  const getGoalPayload = (isCreating: boolean): ActionData => {
    const addressesRef = selectedAddresses?.map((x) => x.resourceType + '/' + x?.id);
    const targetToUse = latestTargetRef.current ?? target;
    const filterTargets = targetToUse?.filter((e) => !deepIsEmpty(e));
    const effectiveEndDate =
      (filterTargets?.[0]?.dueDate as string | undefined) ??
      (endDate?.isValid() ? endDate.format('YYYY-MM-DD') : null);
    const goalDescriptionCoding = goalToEdit?.getDescriptionCoding?.() 
      || selectedPlanDefinition?.getGoalDescriptionCoding?.();
    
    const descriptionCoding = goalDescriptionCoding && goalDescriptionCoding.code
      ? {
          code: goalDescriptionCoding.code,
          display: goalDescriptionCoding.display || goalDescriptionDisplay || description,
          system: goalDescriptionCoding.system
        }
      : undefined;

    return {
      payload: {
        description,
        comments,
        categoryCode: selectedPathway?.code,
        categoryDisplay: selectedPathway?.display,
        lifecycleStatus: selectedLifeCycleStatus?.code,
        achievementStatus: selectedAchievementStatus?.code ?? null,
        achievementStatusText: selectedAchievementStatus?.display ?? null,
        achievementStatusSystem: selectedAchievementStatus?.system ?? null,
        startDate: startDate?.isValid() ? startDate.format('YYYY-MM-DD') : null,
        endDate: effectiveEndDate,
        addressesRef,
        target: (filterTargets && filterTargets?.length > 0) ? filterTargets : null,
        ...(descriptionCoding ? { descriptionCoding } : {}),
        ...(isCreating
          ? {
              patientId: patient?.id,
              patientName: patient?.getFullName?.() ?? null,
              carePlanId: carePlan?.id,
              planDefinitionId: selectedPlanDefinition?.id,
              ownerId: taskOwner?.id ? `${taskOwner?.resourceType}/${taskOwner?.id}` : undefined,
              ownerDisplay: getOwnerDisplayLabel(taskOwner) ?? null,
              relatedGoals: relatedGoals?.length
                ? relatedGoals.map(({ extension }) => extension).flat()
                : undefined,
              taskSuggestions: taskSuggestions
            }
          : {}),
      },
      actionCall: isCreating ? onSuccessfulCreation : onSuccessfulEdit,
      successfulMessage: isCreating ? 'Goal Succesfully Created' : 'Goal Successfully Modified',
    };
  };

  // const goalDescriptionCode =
  //   goalToEdit?.getDescriptionCode?.() ||
  //   selectedPlanDefinition?.getGoalDescriptionCoding?.()?.code;

  // const goalDescriptionDisplay = useMemo(
  //   () =>
  //     goalToEdit?.getDescriptionDisplay?.() ||
  //     selectedPlanDefinition?.getGoalDescriptionCoding?.()?.display,
  //   [goalToEdit, selectedPlanDefinition]
  // );

  const goalDescriptionCode =
    goalToEdit?.getDescriptionCode?.() ||
    selectedPlanDefinition?.getGoalDescriptionCoding?.()?.code;

  const goalDescriptionDisplay =
    goalToEdit?.getDescriptionDisplay?.() ||
    selectedPlanDefinition?.getGoalDescriptionCoding?.()?.display;

  return (
    <CustomModal
      open={open}
      title={
        !goalToEdit
          ? `${i18n('case.details.listofGoals.addNewGoal', 'crs')}`
          : `${i18n('case.details.listofGoals.editGoal', 'crs')}`
      }
      breadcrumbs={[
        'List of Goals',
        !goalToEdit
          ? `${i18n('case.details.listofGoals.addNewGoal', 'crs')}`
          : `${i18n('case.details.listofGoals.editGoal', 'crs')}`,
      ]}
      onSave={handleOnSave}
      onCancel={onClose as Function}
      isLoading={isLoading}
      containerSx={[{ overflow: 'scroll' }, (isSearchMemberOpen || isTaskSuggestionsOpen) ? { width: '94vw' } : {}]}
      childrenWithoutPadding={parentGoal ? <GoalInfoRibbon goal={parentGoal} /> : null}
      {...goalModalProps}
    >
      <Grid container my={3} justifyContent={'space-between'}>
        <Grid item xs={(isSearchMemberOpen || isTaskSuggestionsOpen) ? 6 : 12}>
          {!!patient && (
            <>
              <GridSection mt={0}>
                <GridItem>
                  <TextField
                    fullWidth
                    label={i18n('case.details.listofGoals.organization', 'crs')}
                    defaultValue={patient?.getOrganization()}
                    type="text"
                    placeholder={i18n('case.details.listofGoals.organization', 'crs')}
                    variant="outlined"
                    disabled
                  />
                </GridItem>
                <GridItem>
                  <TextField
                    fullWidth
                    label={i18n('case.details.listofGoals.patientName', 'crs')}
                    defaultValue={patient?.getFullName()}
                    type="text"
                    placeholder={i18n('case.details.listofGoals.patientName', 'crs')}
                    variant="outlined"
                    disabled
                  />
                </GridItem>
              </GridSection>
            </>
          )}
          {!!!goalToEdit && (
            <GridSection>
              <Autocomplete
                disabled={isLoading}
                value={selectedPathway}
                fullWidth
                onChange={(_: React.SyntheticEvent, newValue) => {
                  if (newValue) {
                    updateErrorFieldState({ pathway: null });
                  }
                  updateState({
                    selectedPathway: newValue,
                  });
                }}
                options={sortedPathways}
                getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display as string}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.pathway}
                    helperText={errors.pathway}
                    label={i18n('case.details.listofGoals.category', 'crs')}
                    variant="outlined"
                  />
                )}
              />
            </GridSection>
          )}
          {!!patient && (
            <GridSection>
              <GridItem xs={12}>
                <Autocomplete
                  disabled={isLoading}
                  multiple
                  value={selectedAddresses}
                  fullWidth
                  onChange={(_: React.SyntheticEvent, newValue) => {
                    updateState({
                      selectedAddresses: newValue,
                    });
                  }}
                  options={addresses}
                  getOptionLabel={(option) => {
                    let baseLabel = '';
                    let priorityInfo = '';      
                    if (option.resourceType === 'Observation' && option.valueCodeableConcept) {
                      baseLabel = option?.valueCodeableConcept ? 
                        option.getDisplayValueFromValueCodeableConcept() : "";
                      const interpretation = option?.interpretation ? 
                        option.getInterpretationDisplay() : null;
                      if (interpretation) {
                        priorityInfo = ` (${interpretation})`;
                      }
                    } else if (option.resourceType === 'Condition') {
                      baseLabel = option.code?.text ?? '';
                      const severity = option?.severity && typeof option?.getSeverityDisplay === 'function'
                        ? option.getSeverityDisplay()
                        : null;
                      if (severity) {
                        priorityInfo = ` (${severity})`;
                      }
                    } else {
                      baseLabel = option.code?.text ?? '';
                    }
                    return baseLabel + priorityInfo;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={i18n('case.details.listofGoals.addresses', 'crs')}
                      variant="outlined"
                    />
                  )}
                />
              </GridItem>
            </GridSection>
          )}
          {!!!goalToEdit && (
            <>
              <GridSection>
                <GridItem xs={selectedPlanDefinition ? gridItemColumnSize : 12}>
                  <Autocomplete
                    disabled={isLoading}
                    value={selectedPlanDefinition}
                    fullWidth
                    onChange={(_: React.SyntheticEvent, planDefinition) => {
                      let proposedPathway = pathways.find(
                        ({ code }) => code === planDefinition?.getGoalCategory?.()
                      );
                      const {
                        achievementStatus,
                        lifecycleStatus,
                        goalEndDate,
                        goalStartDate,
                        description,
                      } = getDataFromPlanDefinition(
                        goalStatuses?.asList?.() ?? [],
                        planDefinition,
                        startDate
                      );

                      const patch: Partial<GoalModalState> = {
                        selectedPlanDefinition: planDefinition,
                        ...(lifecycleStatus ? { selectedLifeCycleStatus: lifecycleStatus } : {}),
                        ...(achievementStatus
                          ? {
                              selectedAchievementStatus: achievementStatus,
                            }
                          : {}),
                        ...(description ? { description } : {}),
                        ...(proposedPathway
                          ? { selectedPathway: proposedPathway as ValueSetComposeIncludeConcept }
                          : {}),
                        ...(goalStartDate && goalEndDate
                          ? { startDate: goalStartDate, endDate: goalEndDate }
                          : {}),
                      };
                      if (goalEndDate?.isValid?.()) {
                        const dueDateStr = goalEndDate.format('YYYY-MM-DD');
                        const baseTargets = target && target.length > 0 ? target : [{}];
                        const newTargets = baseTargets.map((t, i) =>
                          i === 0 ? { ...t, dueDate: dueDateStr } : { ...t }
                        );
                        latestTargetRef.current = newTargets;
                        patch.target = newTargets;
                      }
                      updateState(patch);
                      updateTaskSuggestions({ isTaskSuggestionsOpen: planDefinition ? true : false });
                    }}
                    options={filteredPlanDefinitions}
                    getOptionLabel={({ getGoalDescription }: WrappedPlanDefinition) =>
                      getGoalDescription?.() ?? ''
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={i18n('case.details.listofGoals.planDefinition', 'crs')}
                        variant="outlined"
                      />
                    )}
                  />
                </GridItem>
                {!!selectedPlanDefinition && (
                  <GridItem>
                    <SelectSearchMember
                      patient={patient ?? undefined}
                      isLoading={isLoading || !patient}
                      valueSelected={selectedTaskOwner}
                      labelText={"patients.details.tasks.taskOwner"}
                      options={searchMemberOptions?.taskSelectOwner ?? []}
                      selectedOption={'selectedTaskOwner'}
                      stateKey={'taskOwner'}
                      updateSelectedMember={updateTaskOwner}
                      updateState={updateState}
                    />
                  </GridItem>
                )}
              </GridSection>
            </>
          )}
          <GridSection>
            <GridItem>
              <Autocomplete
                disabled={isLoading}
                value={selectedLifeCycleStatus}
                fullWidth
                onChange={(_: React.SyntheticEvent, newValue) => {
                  if (newValue)
                    updateErrorFieldState({
                      lifecycleStatus: null,
                    });
                  updateState({ selectedLifeCycleStatus: newValue });
                }}
                options={goalStatuses?.asList?.() ?? []}
                getOptionLabel={({ display }: GoalLifeCycleStatusOption) => display ?? ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.lifecycleStatus}
                    helperText={errors.lifecycleStatus}
                    label={`${i18n('case.details.listofGoals.status', 'crs')}*`}
                    variant="outlined"
                  />
                )}
              />
            </GridItem>
            <GridItem>
              <Autocomplete
                disabled={isLoading}
                value={selectedAchievementStatus}
                fullWidth
                onChange={(_: React.SyntheticEvent, newValue) => {
                  updateState({
                    selectedAchievementStatus: newValue
                      ? {
                          code: newValue?.code,
                          display: newValue?.display,
                          system: goalAchievementStatuses?.getFirstIncludeConceptSystem?.(),
                        }
                      : null,
                  });
                }}
                options={goalAchievementStatuses?.asList?.() ?? []}
                getOptionLabel={({ display }: GoalAchievementStatusOption) => display ?? ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n('case.details.listofGoals.achievementStatus', 'crs')}
                    variant="outlined"
                  />
                )}
              />
            </GridItem>
          </GridSection>
          <GridSection>
            <GridItem>
              <DatePickerMoment
                disabled={isLoading}
                error={errors.startDate}
                label={i18n('case.details.listofGoals.startDate', 'crs')}
                value={startDate}
                onChange={(value) => {
                  if (value?.isValid?.() || value === null) {
                    updateErrorFieldState({
                      startDate: null,
                    });
                  }
                  updateState({
                    startDate: value,
                  });
                }}
              />
            </GridItem>
            <GridItem>
              <DatePickerMoment
                disabled={isLoading}
                error={errors.endDate}
                label={i18n('case.details.listofGoals.endDate', 'crs')}
                value={endDate}
                onChange={(value) => {
                  if (value?.isValid?.() || value === null) {
                    updateErrorFieldState({
                      endDate: null,
                    });
                  }
                  updateStateWithEndDate(value ?? null);
                }}
              />
            </GridItem>
          </GridSection>

          <ExpandableCard
            title={i18n('case.details.listofGoals.target.title', 'crs')}
            sxProp={{
              mt: 2,
              border: "1px solid",
              borderColor: "#919eab3d",
              borderRadius: 1,
              transition: "border-color 0.3s, box-shadow 0.3s",
              "&:hover": {
                borderColor: "primary.main",
              }
            }}
          >
            <GoalTargets 
              target={target}
              updateState={updateStateForGoalTargets}
              i18n={i18n} 
              configurations={configurations?.fhir}
            />
          </ExpandableCard>

          {!!goalDescriptionCode || !!goalDescriptionDisplay ? (
            <GridSection>
              <GridItem xs={12}>
                <TextField
                  fullWidth
                  disabled
                  value={goalDescriptionDisplay ?? ''}
                  label={i18n('case.details.listofGoals.descriptionCode', 'crs')}
                  variant="outlined"
                />
              </GridItem>
            </GridSection>
          ) : null}

          <GridSection>
            <GridItem xs={12}>
              <TextField
                fullWidth
                disabled={isLoading}
                multiline
                rows={4}
                value={description}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const { value } = event.target;
                  if (value.trim().length) {
                    updateErrorFieldState({ description: null });
                  }
                  updateState({
                    description: value,
                  });
                }}
                label={`${i18n('case.details.listofGoals.description', 'crs')}*`}
                placeholder={`${i18n('case.details.listofGoals.description', 'crs')}*`}
                variant="outlined"
                error={!!errors.description}
                helperText={errors.description}
              />
            </GridItem>
          </GridSection>

          <GridSection>
            <TextField
              fullWidth
              disabled={isLoading}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                updateState({
                  comments: event.target.value,
                });
              }}
              value={comments}
              label={i18n('case.details.listofGoals.comments', 'crs')}
              placeholder={i18n('case.details.listofGoals.comments', 'crs')}
              variant="outlined"
            />
          </GridSection>
        </Grid>
        {isSearchMemberOpen && (
          <Grid item xs={5.9}>
            <SearchMember
              patient={patient as WrappedPatient}
              onSelectResource={(resource) => {
                updateTaskOwner({ selectedTaskOwner: resource, isSearchMemberOpen: false });
                updateState({ taskOwner: resource });
              }}
              onClear={() => {
                updateTaskOwner({ selectedTaskOwner: null, isSearchMemberOpen: false });
                updateState({ taskOwner: null });
              }}
              externalResourceType={(() => {
                const selectedOption = selectedTaskOwner as TaskSelectedMemberOption;
                return selectedOption?.value as SearchMemberProps['externalResourceType'];
              })()}
            />
          </Grid>
        )}
        {isTaskSuggestionsOpen && (
          <Grid item xs={5.9} sx={{ display: isSearchMemberOpen ? 'none' : 'unset'}}>
            <TaskSuggestionsGrid
              isTaskSuggestionsOpen={isTaskSuggestionsOpen}
              title={i18n('case.details.listofGoals.taskSuggestions.title','crs')}
              onTaskSuggestionAction={(taskSuggestions: TaskSuggestion) => 
                updateState({ taskSuggestions: taskSuggestions}
              )}
              planDefinition={selectedPlanDefinition}
              startDate={startDate}
              taskOwner={taskOwner}
              configurations={componentsData?.goal?.tasksSuggestions}
            />
          </Grid>
        )}
      </Grid>
    </CustomModal>
  );
};

const getDataFromPlanDefinition = (
  goalStatuses: GoalLifeCycleStatusOption[],
  planDefinition: WrappedPlanDefinition | null,
  startDate: moment.Moment | null = null
): GoalDataFromPlanDefinition => {
  const lifecycleStatus = getValueSetConceptValue(
    goalStatuses ?? [],
    planDefinition?.getGoalLifecycleStatus?.()
  );

  const achievementStatus = planDefinition?.getGoalAchievementStatus?.();

  const goalDuration = planDefinition?.getGoalDuration?.();

  let goalStartDate: moment.Moment | null = null;
  let goalEndDate: moment.Moment | null = null;
  if (goalDuration?.unit && goalDuration?.value) {
    goalStartDate = startDate?.isValid?.() ? startDate : moment();
    goalEndDate = goalStartDate
      .clone()
      .add(goalDuration.value, goalDuration.unit as DurationInputArg2);
  }

  const description = planDefinition?.getGoalDescription?.() ?? '';

  return {
    lifecycleStatus,
    achievementStatus,
    goalStartDate,
    goalEndDate,
    description,
  };
};

export type GoalLifeCycleStatusOption = ValueSetComposeIncludeConcept;
export type GoalAchievementStatusOption = Coding;

export default GoalModal;
